import { readFileSync } from "fs";
import crypto from "node:crypto";
import { join } from "path";
import { HONEYBADGER_IGNORED_STATUS_CODES, INITIAL_VECTOR, SALT, SECRET_KEY, HONEYBADGER_IGNORED_ERROR_MESSAGES, } from "@constants/common";
import { ERRORS } from "@constants/texts/errors";
import Honeybadger from "@honeybadger-io/js";
import { AxiosError } from "axios";
import consoleLogFormatted from "consoleLogFormatted";
import { camelToSnakeCase } from "neetocist";
export const createShardObject = ({ currentShard = 0, status = "running", duration = null, }) => ({
    [currentShard]: { status, duration },
});
export const convertBufferToBlob = (buffer, contentType) => new Blob([Uint8Array.from(buffer)], { type: contentType });
export const waitUntilTimeout = (timeout) => new Promise(resolve => setTimeout(resolve, timeout));
export const waitUntilCondition = async (condition, timeout = 6.5 * 60 * 1000 // Default timeout after 6.5 minutes
) => {
    const startTime = new Date();
    const timeoutTime = new Date(startTime.getTime() + timeout);
    while (!condition()) {
        if (new Date().getTime() >= timeoutTime.getTime()) {
            consoleLogFormatted.error(ERRORS.common.timeoutExceeded);
            consoleLogFormatted.error(condition.toString());
            return;
        }
        await waitUntilTimeout(100); // Poll every 100 milliseconds until timeout
    }
};
export const convertToSha1 = (text) => crypto.createHash("sha1").update(text).digest("hex").slice(0, 20);
export const generateHistoryId = ({ projectId, specName, describeTitles, testTitle, }) => {
    const titles = [...describeTitles, testTitle];
    const targetString = `[project=${projectId}]${specName}\x1e${titles.join("\x1e")}`;
    const targetStringSha = convertToSha1(targetString);
    const pathNameSha = convertToSha1(specName);
    return `${pathNameSha}-${targetStringSha}`;
};
export const joinHyphenCase = (...strings) => strings.join("-");
export const convertTopLevelKeysToSnakeCase = (object) => Object.entries(object).reduce((acc, curr) => {
    const snakeCaseKey = camelToSnakeCase(curr[0]);
    acc[snakeCaseKey] = curr[1];
    return acc;
}, {});
export const decryptKey = (encryptedKey) => {
    const iv = Buffer.from(INITIAL_VECTOR, "hex");
    const salt = Buffer.from(SALT, "hex");
    const secretKey = SECRET_KEY;
    try {
        const key = crypto.scryptSync(secretKey, Uint8Array.from(salt), 32);
        const keyObject = crypto.createSecretKey(Uint8Array.from(key));
        const ivView = Uint8Array.from(iv);
        const decipher = crypto.createDecipheriv("aes-256-cbc", keyObject, ivView);
        let decrypted = decipher.update(encryptedKey, "hex", "utf8");
        decrypted += decipher.final("utf8");
        return decrypted;
    }
    catch (error) {
        consoleLogFormatted.error("Failed to decrypt API key: " + error);
        throw new Error("Failed to initialize reporting");
    }
};
export const getAxiosError = (error) => {
    if (error instanceof AxiosError)
        return error;
    if (error instanceof Error) {
        const originalError = error.originalError;
        if (originalError instanceof AxiosError)
            return originalError;
    }
    return undefined;
};
export const extractApiErrorMessage = (error) => {
    var _a;
    const axiosError = getAxiosError(error);
    if (axiosError) {
        const data = (_a = axiosError.response) === null || _a === void 0 ? void 0 : _a.data;
        if (typeof data === "string" && data.trim())
            return data.trim();
        if (data && typeof data === "object") {
            const body = data;
            if (typeof body.error === "string")
                return body.error;
            if (typeof body.message === "string")
                return body.message;
        }
        return undefined;
    }
    if (error instanceof Error && error.message)
        return error.message;
    return undefined;
};
export const isErrorCodeExempted = (status = 422) => HONEYBADGER_IGNORED_STATUS_CODES.includes(status);
export const isErrorMessageExempted = (message) => {
    if (!message)
        return false;
    return HONEYBADGER_IGNORED_ERROR_MESSAGES.some(errorMessage => message.includes(errorMessage));
};
export const reportToHoneybadger = async ({ context, error, title, }) => {
    var _a;
    if (error instanceof AggregateError) {
        consoleLogFormatted.error(`AggregateError occurred with ${error.errors.length} individual errors:`);
        error.errors.forEach((err, index) => {
            consoleLogFormatted.error(`--- Error ${index + 1} ---`);
            consoleLogFormatted.error(`Message: ${err.message || "No message"}`);
            consoleLogFormatted.error(`Code: ${err.code || "No code"}`);
            consoleLogFormatted.error(`Name: ${err.name || "Unknown"}`);
            if (err.stack) {
                consoleLogFormatted.error(`Stack: ${err.stack.split("\n")[0]}`);
            }
            consoleLogFormatted.error("--------------------");
        });
        Honeybadger.setContext({
            ...context,
            errorDetails: {
                aggregateErrorCount: error.errors.length,
                individualErrors: error.errors.map(err => ({
                    message: err.message,
                    code: err.code,
                    name: err.name,
                })),
            },
            reporterVersion: getCurrentReporterVersion(),
        });
        return;
    }
    const response = (_a = getAxiosError(error)) === null || _a === void 0 ? void 0 : _a.response;
    const data = response === null || response === void 0 ? void 0 : response.data;
    Honeybadger.setContext({
        ...context,
        errorDetails: error,
        reporterVersion: getCurrentReporterVersion(),
    });
    if (!isErrorCodeExempted(response === null || response === void 0 ? void 0 : response.status) &&
        !isErrorMessageExempted(data === null || data === void 0 ? void 0 : data.error)) {
        await Honeybadger.notifyAsync((data === null || data === void 0 ? void 0 : data.error) ? data.error : error, title);
    }
};
export const getCurrentReporterVersion = () => {
    const packageJson = JSON.parse(readFileSync(join(__dirname, "./package.json"), "utf8"));
    return packageJson.version;
};
export const testLogPrefix = (historyId) => {
    const time = new Date().toISOString().slice(0, 19).replace("T", " ");
    return `${time}Z [${historyId}]`;
};
export const humanizeTime = (milliseconds) => {
    const time = {
        hour: Math.floor(milliseconds / (60 * 60 * 1000)),
        minute: Math.floor(milliseconds / (60 * 1000)) % 60,
        second: Math.floor(milliseconds / 1000) % 60,
        millisecond: Math.floor(milliseconds) % 1000,
    };
    return Object.entries(time)
        .filter(([_, value]) => value !== 0)
        .map(([unit, value]) => value + " " + (value !== 1 ? `${unit}s` : unit))
        .join(", ");
};
export const resolveReporterOptions = (options) => {
    var _a, _b, _c;
    const { env } = process;
    return {
        projectKey: options.projectKey || env.NEETO_PROJECT_KEY || "",
        baseURL: options.baseURL || env.NEETO_PLAYDASH_API_BASE_URL || "",
        apiKey: options.apiKey || env.NEETO_API_KEY || "",
        ciBuildId: options.ciBuildId || env.NEETO_CI_BUILD_ID || "",
        tags: options.tags || ((_a = env.NEETO_PLAYDASH_TAGS) === null || _a === void 0 ? void 0 : _a.split(",")) || [],
        title: options.title || env.NEETO_PLAYDASH_TITLE,
        commitId: options.commitId || env.NEETO_PLAYDASH_COMMIT_ID,
        author: options.author || env.NEETO_PLAYDASH_AUTHOR,
        branch: options.branch || env.NEETO_PLAYDASH_BRANCH,
        smartOrchestration: (_b = options.smartOrchestration) !== null && _b !== void 0 ? _b : env.NEETO_PLAYDASH_SMART_ORCHESTRATION !== "false",
        groupBySpecs: (_c = options.groupBySpecs) !== null && _c !== void 0 ? _c : env.NEETO_PLAYDASH_GROUP_BY_SPECS === "true",
    };
};
//# sourceMappingURL=common.js.map